import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import * as sodium from 'libsodium-wrappers';
import { WebsocketService } from '../../real-time/services/websocket.service';
import { KeyStoreService } from './keystore.service';
import { WsEnvelope } from '@app/areas/real-time/models/websocket.models';
import { ChatApiService } from './chat-api.service';

export interface IncomingKeyPayload {
  issuedAt: number;
  issuer: string;
  keys: { convId: string; keyB64: string }[];
  pairToken: string;
}

const HKDF_SALT = new TextEncoder().encode('GroupChatSalt');
const HKDF_INFO = new Uint8Array(0);
const DEBUG_CHAT_AUTH = false;
const _b64O = (u: Uint8Array | ArrayBuffer | undefined | null) => {
  try {
    if (!u) return '∅';
    const arr = u instanceof Uint8Array ? u : new Uint8Array(u);
    // prefer libsodium if present for variant stability
    // @ts-ignore
    return (window as any).sodium
      // @ts-ignore
      ? (window as any).sodium.to_base64(arr, (window as any).sodium.base64_variants.ORIGINAL)
      : btoa(String.fromCharCode(...arr));
  } catch { return '∅'; }
};
const dbgAuth = (...args: any[]) => { if (DEBUG_CHAT_AUTH) console.log('[ChatAuth]', ...args); };

@Injectable({ providedIn: 'root' })
export class ChatAuthService {
  private readonly TAG = '[ChatAuthService]';
  private readonly ws = inject(WebsocketService);
  private readonly keys = inject(KeyStoreService);

  private isAuthenticatedSubject = new BehaviorSubject<boolean>(true);
  readonly isAuthenticated$ = this.isAuthenticatedSubject.asObservable();

  private readonly ID_PRIV = 'x25519-priv';
  private readonly ID_PUB  = 'x25519-pub';

  // ───────────────────────── base64 & key helpers ─────────────────────────
  private b64Flex(b64: string): Uint8Array {
    const V = sodium.base64_variants;
    const s = (b64 || '').trim();
    for (const v of [V.ORIGINAL, V.ORIGINAL_NO_PADDING, V.URLSAFE, V.URLSAFE_NO_PADDING]) {
      try {
        const out = sodium.from_base64(s, v);
        dbgAuth('b64Flex ok', { inLen: s.length, variant: v, outLen: out.length });
        return out;
      } catch {}
    }
    const fixed = s.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((s.length + 3) % 4);
    const out = sodium.from_base64(fixed, V.ORIGINAL);
    dbgAuth('b64Flex repaired', { inLen: s.length, outLen: out.length });
    return out;
  }

  private x25519FromB64(b64: string): Uint8Array {
    const b = this.b64Flex(b64);
    if (b.length === 32) { dbgAuth('x25519FromB64: raw32'); return b; }
    for (let i = 0; i + 35 <= b.length; i++) {
      if (b[i] === 0x03 && b[i+1] === 0x21 && b[i+2] === 0x00) {
        dbgAuth('x25519FromB64: DER BIT STRING -> raw32');
        return b.subarray(i + 3, i + 35);
      }
    }
    dbgAuth('x25519FromB64: fallback last32');
    return b.subarray(Math.max(0, b.length - 32));
  }
  private async hkdfRaw(myPriv: Uint8Array, peerPubB64: string): Promise<Uint8Array> {
    await sodium.ready;
    const peerPub = this.x25519FromB64(peerPubB64);
    const shared  = sodium.crypto_scalarmult(myPriv, peerPub);
    const hkdfKey = await crypto.subtle.importKey('raw', shared, 'HKDF', false, ['deriveBits']);
    const bits    = await crypto.subtle.deriveBits(
      { name: 'HKDF', hash: 'SHA-256', salt: HKDF_SALT, info: HKDF_INFO }, hkdfKey, 256
    );
    const out = new Uint8Array(bits);
    dbgAuth('hkdfRaw', { peerPubLen: peerPub.length, outB64: _b64O(out).slice(0, 16) + '…' });
    return out;
  }

  private async hkdfAesKey(myPriv: Uint8Array, peerPubB64: string): Promise<CryptoKey> {
    const raw = await this.hkdfRaw(myPriv, peerPubB64);
    return crypto.subtle.importKey('raw', raw, { name: 'AES-GCM', length: 256 }, false, ['encrypt','decrypt']);
  }

  // ───────────────────────── central 32-byte extractor ─────────────────────────
  /**
   * Return the 32-byte group key from a Base64 blob:
   * - If `b64` is raw 32B → return as-is.
   * - If AES-GCM envelope (~60B) → try creator→me, else self→me.
   */
  async toKey32(b64: string, creatorPubB64?: string): Promise<Uint8Array> {
    await sodium.ready;

    if (!localStorage.getItem(this.ID_PRIV) || !localStorage.getItem(this.ID_PUB)) {
      await this.getOrCreateDeviceKeypair();
    }
    const myPriv   = this.b64Flex(localStorage.getItem(this.ID_PRIV)!);
    const myPubB64 = localStorage.getItem(this.ID_PUB)!;

    const blob = this.b64Flex(b64);
    dbgAuth('toKey32 start', { envLen: blob.length });
    if (blob.length === 32) { dbgAuth('toKey32: raw32 passthrough'); return blob; }

    const iv   = blob.slice(0, 12);
    const data = blob.slice(12);

    let kCreatorRaw: Uint8Array | undefined;
    let kSelfRaw: Uint8Array | undefined;

    try { if (creatorPubB64) kCreatorRaw = await this.hkdfRaw(myPriv, creatorPubB64); }
    catch (e) { dbgAuth('toKey32 hkdf(creator) failed', e); }

    try { kSelfRaw = await this.hkdfRaw(myPriv, myPubB64); }
    catch (e) { dbgAuth('toKey32 hkdf(self) failed', e); }

    dbgAuth('toKey32 keys', {
      hkdfCreator: _b64O(kCreatorRaw)?.slice(0, 16) + '…',
      hkdfSelf: _b64O(kSelfRaw)?.slice(0, 16) + '…'
    });

    if (kCreatorRaw) {
      try {
        const k = await crypto.subtle.importKey('raw', kCreatorRaw, 'AES-GCM', false, ['decrypt']);
        const raw = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, k, data);
        const out = new Uint8Array(raw);
        dbgAuth('toKey32 decrypt creator ok', { outLen: out.length });
        if (out.length === 32) return out;
      } catch (e) {
        dbgAuth('toKey32 decrypt creator failed', e);
      }
    }

    if (kSelfRaw) {
      try {
        const k = await crypto.subtle.importKey('raw', kSelfRaw, 'AES-GCM', false, ['decrypt']);
        const raw = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, k, data);
        const out = new Uint8Array(raw);
        dbgAuth('toKey32 decrypt self', { outLen: out.length });
        if (out.length !== 32) throw new Error('unwrap length != 32');
        return out;
      } catch (e) {
        dbgAuth('toKey32 decrypt self failed', e);
        throw e;
      }
    }

    throw new Error('HKDF derivation failed (no candidate keys)');
  }

  // ───────────────────────── explicit wrappers (kept for callers) ─────────────────────────
  async unwrapGroupKeyForMe(envelopeB64: string): Promise<Uint8Array> {
    dbgAuth('unwrapGroupKeyForMe');
    return this.toKey32(envelopeB64);
  }

  async unwrapGroupKeyFromCreator(creatorPubB64: string, envelopeB64: string): Promise<Uint8Array> {
    dbgAuth('unwrapGroupKeyFromCreator');
    return this.toKey32(envelopeB64, creatorPubB64);
  }

  async wrapGroupKeyForRecipient(recipientPubB64: string, groupKey32: Uint8Array): Promise<string> {
    await sodium.ready;
    if (!localStorage.getItem(this.ID_PRIV)) await this.getOrCreateDeviceKeypair();
    const myPriv = this.b64Flex(localStorage.getItem(this.ID_PRIV)!);

    const aesKey = await this.hkdfAesKey(myPriv, recipientPubB64);
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, aesKey, groupKey32);
    const out = new Uint8Array(iv.byteLength + ct.byteLength);
    out.set(iv, 0); out.set(new Uint8Array(ct), iv.byteLength);
    return sodium.to_base64(out, sodium.base64_variants.ORIGINAL);
  }

  // ───────────────────────── device identity ─────────────────────────
  async getOrCreateDeviceKeypair(): Promise<{ pub: Uint8Array; priv: Uint8Array }> {
    await sodium.ready;
    const privB64 = localStorage.getItem(this.ID_PRIV);
    const pubB64  = localStorage.getItem(this.ID_PUB);
    if (privB64 && pubB64) {
      return { priv: sodium.from_base64(privB64), pub: sodium.from_base64(pubB64) };
    }
    const kp = sodium.crypto_box_keypair(); // X25519
    localStorage.setItem(this.ID_PRIV, sodium.to_base64(kp.privateKey));
    localStorage.setItem(this.ID_PUB,  sodium.to_base64(kp.publicKey));
    console.log(this.TAG, '🔑 Created new browser device keypair');
    return kp;
  }

  async ensureDeviceKeyOnServer(api: ChatApiService, deviceId: string): Promise<string> {
    await sodium.ready;
    const { pub } = await this.getOrCreateDeviceKeypair();
    const publicKeyB64 = sodium.to_base64(pub, sodium.base64_variants.ORIGINAL);
    await firstValueFrom(api.rotatePublicKey({ deviceId, publicKeyB64 }));
    return publicKeyB64;
  }

  // ───────────────────────── misc app wiring ─────────────────────────
  async checkExistingAuth(): Promise<void> {
    const hasIdentity = !!localStorage.getItem(this.ID_PRIV);
    const anyKeys = this.hasAnyKeysStored();
    if (!hasIdentity && !anyKeys) this.isAuthenticatedSubject.next(false);
  }

  async listenForKeys(userId: string): Promise<void> {
    const topic = `chat.auth.${userId}`;
    if (this.ws.status !== WebSocket.OPEN) await this.ws.connect();
    this.ws.joinTopic(topic);

    this.ws.messages$.subscribe(async (envelope: WsEnvelope) => {
      if (envelope.target.topic !== topic) return;
      const body = envelope.body as IncomingKeyPayload | undefined;
      if (!body || !Array.isArray(body.keys)) return;

      console.log(`${this.TAG} 🔑 Received key batch from ${body.issuer} count=${body.keys.length}`);
      let storedAny = false;
      for (const k of body.keys) {
        try {
          const raw = sodium.from_base64(k.keyB64).buffer;
          await this.keys.save(raw, k.convId);
          storedAny = true;
        } catch (e) {
          console.error(`${this.TAG} ❌ Failed to store key for conv ${k.convId}`, e);
        }
      }
      if (storedAny) this.isAuthenticatedSubject.next(true);
    });
  }

  async generateConversationKey(): Promise<Uint8Array> {
    await sodium.ready;
    return sodium.randombytes_buf(32);
  }

  private hasAnyKeysStored(): boolean {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith('key-')) return true;
    }
    return false;
  }
}
